/**
 * useTorreControlData — orquesta TODO lo que la Torre de control muestra en
 * vivo, reutilizando los servicios/hooks ya existentes (NO instancia axios a
 * mano, NO inventa endpoints):
 *
 *   · dashboardService.getOperationalSummary  → nivel de servicio, cumplimiento,
 *     tiempo promedio, en-ruta por comuna/tipo (company-wide, cacheado en Redis).
 *   · dashboardService.getPendingBreakdown    → desglose de pendientes.
 *   · dashboardService.getDriverPerformance   → ranking de repartidores.
 *   · getPackagesByStatusCount                → conteo por estado del día.
 *   · getAllIncidents                         → incidencias recientes (alertas).
 *
 * El mapa + el estado de rutas/repartidores en vivo NO se piden acá: se sirven
 * del adapter `useActividadData({date})` (polling de paquetes/repartidores del
 * día + socket de posiciones `/location`), igual que /admin/live-routes. La
 * vista compone ambos.
 *
 * GAPS de backend (documentados; la UI los marca con placeholder/aviso):
 *   · No existe un endpoint nativo "control-tower": esto es composición
 *     client-side de varios reportes. Un `reports/control-tower/live` que
 *     devolviera el snapshot completo en una llamada ahorraría 4-5 requests.
 *   · operational-summary es company-wide (ignora companyId — ver
 *     BEARTRACK-CONTEXT §5). No hay filtro por cliente para NS/atención.
 *   · No hay "SLA en riesgo / próximas N horas": el back no expone deadlines por
 *     paquete. La tarjeta de riesgo queda como pendiente del back.
 *   · driver-performance no trae estado en vivo del repartidor (online/idle);
 *     eso sale del socket vía useActividadData.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAuth from '../../../hooks/useAuth';
import { dashboardService } from '../../../services/dashboardService';
import { getPackagesByStatusCount, getAllIncidents } from '../../../api/packages';
import { getOperationTimezone } from '../../../utils/timezone';
import { deliveredFromResume } from '../../../utils/packagesResume';

const AUTO_REFRESH_MS = 90000;

// Derivación canónica de la empresa logística del usuario admin. La comparte
// el shell de TorreControl (gate del empty-state) para que el "¿hay operación
// que monitorear?" y el "¿fetcheo datos?" sean SIEMPRE la misma regla.
export function getLogisticCompanyId(user) {
  return user?.logisticCompany?._id || user?.logisticCompanyId || null;
}

function safePct(part, total) {
  return total > 0 ? (part / total) * 100 : 0;
}

export default function useTorreControlData({ date }) {
  const { user } = useAuth();
  const logisticCompanyId = useMemo(() => getLogisticCompanyId(user), [user]);
  const tz = useMemo(() => getOperationTimezone(user), [user]);

  const [op, setOp] = useState(null);            // operational-summary
  const [counts, setCounts] = useState(null);    // status-count del día
  const [pending, setPending] = useState(null);  // pending-breakdown
  const [drivers, setDrivers] = useState([]);    // driver-performance
  const [incidents, setIncidents] = useState([]); // alertas recientes
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const firstLoad = useRef(true);

  const fetchAll = useCallback(
    async (manual = false) => {
      if (!logisticCompanyId) return;
      if (manual || !firstLoad.current) setRefreshing(true);
      if (manual) setError(null);

      // operational-summary es la fuente de los KPIs operativos: si falla, la
      // banda no tiene datos → lo tratamos como error de la vista. El resto
      // (conteos, pendientes, ranking, incidencias) es best-effort y degrada a
      // estado vacío sin tumbar la pantalla.
      try {
        const resp = await dashboardService.getOperationalSummary(logisticCompanyId, date, tz);
        setOp(resp?.data ?? null);
        setError(null);
      } catch (err) {
        setOp(null);
        setError(
          err?.response?.data?.message ||
            'No se pudo cargar el resumen operativo. Reintenta en unos segundos.',
        );
      }

      const settle = async (promise, onOk, onFail) => {
        try {
          onOk(await promise);
        } catch {
          onFail();
        }
      };

      await Promise.all([
        settle(
          getPackagesByStatusCount({ logisticCompanyId, startDate: date, endDate: date }),
          (c) =>
            setCounts({
              picked: c?.status1 || 0,
              warehouse: c?.status2 || 0,
              route: c?.status3_30 || 0,
              delivered: deliveredFromResume(c),
              failed: c?.status6_7 || 0,
              returns: c?.status21_22 || 0,
            }),
          () => setCounts(null),
        ),
        settle(
          dashboardService.getPendingBreakdown(logisticCompanyId, date, date, tz),
          (r) => setPending(r?.data ?? null),
          () => setPending(null),
        ),
        settle(
          dashboardService.getDriverPerformance(logisticCompanyId, date, date, tz),
          (r) => {
            const data = r?.data;
            const list = Array.isArray(data) ? data : data?.drivers || data?.docs || [];
            setDrivers(Array.isArray(list) ? list : []);
          },
          () => setDrivers([]),
        ),
        settle(
          getAllIncidents({ page: 1, limit: 8, orderField: 'createdAt', sortType: 'desc' }),
          (res) => setIncidents(res?.success ? res.data?.docs || [] : []),
          () => setIncidents([]),
        ),
      ]);

      setLastUpdated(new Date());
      if (firstLoad.current) {
        firstLoad.current = false;
        setLoading(false);
      }
      setRefreshing(false);
    },
    [logisticCompanyId, date, tz],
  );

  useEffect(() => {
    firstLoad.current = true;
    setLoading(true);
  }, [date]);

  useEffect(() => {
    if (!logisticCompanyId) {
      setLoading(false);
      return undefined;
    }
    fetchAll();
    const id = setInterval(() => {
      if (!document.hidden) fetchAll(false);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [logisticCompanyId, fetchAll]);

  // ── KPIs derivados (forma estable para la banda) ──────────────────────────
  const kpis = useMemo(() => {
    const sl = op?.serviceLevel;
    const comp = op?.packagesPercentage;
    return {
      serviceLevelPct: Math.round((sl?.serviceLevelPct || 0) * 10) / 10,
      serviceLevelBefore: sl?.deliveredBeforeCutoff || 0,
      serviceLevelTotal: sl?.total || 0,
      compliancePct: Math.round((comp?.percentage || 0) * 10) / 10,
      // Sin `counts` (falló la request) se cae a operational-summary, como antes. Con
      // `counts` pero `delivered: null` (el backend no pudo contar) se conserva el null
      // para mostrar "sin dato": ese respaldo cuenta otra cosa (recolectados hoy y ya
      // entregados), no lo entregado hoy.
      delivered: counts ? counts.delivered : comp?.delivered ?? 0,
      total: comp?.total || 0,
      failed: counts?.failed ?? 0,
      route: counts?.route ?? 0,
      avgDeliveryMs: op?.averageDeliveryTime?.averageDeliveryTime || 0,
    };
  }, [op, counts]);

  // Desglose de pendientes — el back devuelve formas distintas según versión;
  // normalizamos a { label, count }[] y total.
  const pendingBreakdown = useMemo(() => {
    if (!pending) return { total: 0, rows: [] };
    const raw = pending.breakdown || pending.buckets || pending.items || pending;
    const rows = [];
    let total = 0;
    if (Array.isArray(raw)) {
      raw.forEach((it) => {
        const count = Number(it.count ?? it.value ?? it.total ?? 0) || 0;
        total += count;
        rows.push({ label: it.label || it.name || it.reason || it.status || '—', count });
      });
    } else if (raw && typeof raw === 'object') {
      Object.entries(raw).forEach(([label, count]) => {
        const n = Number(count) || 0;
        if (Number.isFinite(n) && typeof count !== 'object') {
          total += n;
          rows.push({ label, count: n });
        }
      });
    }
    rows.sort((a, b) => b.count - a.count);
    return { total: pending.total ?? total, rows: rows.slice(0, 6) };
  }, [pending]);

  return {
    logisticCompanyId,
    loading,
    refreshing,
    error,
    lastUpdated,
    refetch: () => fetchAll(true),
    op,
    kpis,
    counts,
    pendingBreakdown,
    drivers,
    incidents,
    safePct,
  };
}
